/**
 * WorkflowRailContent
 * Desktop-only always-expanded inline view for the workflow rail.
 *
 * Layout: each workflow renders as a card with a vertical list of member stage rows.
 *   Completed member  → green left-border accent, collapsed "Complete" row
 *   Active member     → primary left-border accent, expanded with all steps + "next" emphasis
 *   Upcoming member   → muted/locked, "Unlocks after [prev]" — no steps shown
 *
 * Framework note: upcoming members return preconditions: [] because their instance
 * hasn't been created yet. Step labels for upcoming members require an API change
 * (speculative resolution). Only totalSteps is available.
 *
 * data-testid attributes match MemberDetailView's contract so integration tests
 * targeting precondition/action locators remain stable.
 */

import { realUnitNumber } from '@resida/shared';
import { CheckCircle2, Lock, ArrowRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import { WorkflowActionButton } from './WorkflowActionButton';
import { useWorkflowPanel } from './useWorkflowPanel';
import { DismissWorkflowButton } from './DismissWorkflowButton';
import { Skeleton } from '../ui/skeleton';
import {
  ATOMIC_WORKFLOW_LABELS,
  SAGA_FALLBACK_LABELS,
  type SagaWorkflowSummary,
  type AtomicWorkflowSummary,
  type MemberWorkflowSummary,
  type Precondition,
} from '../../types/workflow';

const MEMBER_TYPE_LABELS: Record<string, string> = {
  lease_setup: 'Lease Setup',
  payment_setup: 'Payment Setup',
  move_in_walkthrough: 'Move-In Walkthrough',
  move_out_walkthrough: 'Move-Out Walkthrough',
};

// ── Step rows within the active member stage ───────────────────────────────────

/**
 * Single precondition row inside the active stage.
 *
 * isNext: the first unsatisfied item — gets an arrow indicator and its CTA button.
 * Later unsatisfied items are dimmed (○ dot, no CTA) so the queue is visible
 * without competing for attention with the immediate action.
 *
 * Prerequisites (kind='prerequisite') hide once satisfied (BR-JO-08).
 * Steps (kind='step') stay visible with a checkmark when done.
 */
function StageStepRow({
  precondition,
  index,
  isNext,
}: {
  precondition: Precondition;
  index: number;
  isNext: boolean;
}) {
  const isPrereq = (precondition.kind ?? 'prerequisite') === 'prerequisite';

  if (isPrereq && precondition.satisfied) return null;

  if (precondition.satisfied) {
    return (
      <div
        data-testid={`workflow-step-${index}`}
        className="flex items-center gap-2.5 py-1.5"
        role="listitem"
      >
        <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" aria-hidden="true" />
        <span className="text-xs text-muted-foreground">{precondition.label}</span>
      </div>
    );
  }

  return (
    <div
      data-testid={isPrereq ? `workflow-precondition-${index}` : `workflow-step-${index}`}
      className={`flex items-start gap-2.5 py-2 ${!isNext ? 'opacity-50' : ''}`}
      role="listitem"
    >
      {isNext ? (
        <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
      ) : (
        <span className="flex items-center justify-center h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true">
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-xs leading-snug ${isNext ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
          {precondition.label}
        </p>
        {!precondition.satisfied && precondition.remediation && (
          <div className="mt-2">
            <WorkflowActionButton
              action={precondition.remediation}
              testId={`workflow-precondition-cta-${index}`}
              disabled={false}
              size="sm"
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Member stage rows ──────────────────────────────────────────────────────────

function CompletedStageRow({ member }: { member: MemberWorkflowSummary }) {
  const label = MEMBER_TYPE_LABELS[member.type] ?? member.type;
  return (
    <div className="flex items-center gap-2.5 pl-3 pr-3 py-2.5 border-l-4 border-success bg-success/5 rounded-r-md">
      <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" aria-hidden="true" />
      <span className="text-xs font-medium text-success flex-1">{label}</span>
      <span className="text-[10px] font-medium text-success/70">Complete</span>
    </div>
  );
}

function UpcomingStageRow({
  member,
  prevLabel,
}: {
  member: MemberWorkflowSummary;
  prevLabel?: string;
}) {
  const label = MEMBER_TYPE_LABELS[member.type] ?? member.type;
  return (
    <div className="pl-3 pr-3 py-2.5 border-l-4 border-border/40 rounded-r-md" aria-label={`${label} — locked`}>
      <div className="flex items-center gap-2 opacity-50">
        <Lock className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      {prevLabel && (
        <p className="text-[10px] text-muted-foreground/60 mt-0.5 ml-5">
          Unlocks after {prevLabel}
        </p>
      )}
    </div>
  );
}

function ActiveStageRow({ member }: { member: MemberWorkflowSummary }) {
  const label = MEMBER_TYPE_LABELS[member.type] ?? member.type;

  const allSatisfied = member.preconditions.every((p) => p.satisfied);
  const firstUnsatisfiedIdx = member.preconditions.findIndex((p) => !p.satisfied);

  // A row is visible if it's not a satisfied prerequisite
  const hasVisibleRows = member.preconditions.some(
    (p) => !((p.kind ?? 'prerequisite') === 'prerequisite' && p.satisfied),
  );

  // No steps at all — outcome action is the only thing to do
  const noSteps = member.preconditions.length === 0;

  return (
    <div className="border-l-4 border-primary rounded-r-md overflow-hidden">
      {/* Stage header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-primary/[0.06]">
        <span className="text-xs font-semibold text-foreground flex-1 min-w-0 truncate">
          {label}
        </span>
        <Badge
          variant="primary"
          className="px-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shrink-0"
        >
          Now
        </Badge>
        {member.currentStep !== null && (
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
            {member.stepLabel ? `${member.stepLabel} · ` : ''}
            {member.currentStep}/{member.totalSteps}
          </span>
        )}
      </div>

      {/* Step rows — all preconditions visible so the user sees the full queue */}
      {hasVisibleRows && (
        <div className="px-3 py-0.5 bg-primary/[0.02]" role="list" aria-label="Steps">
          {member.preconditions.map((p, i) => (
            <StageStepRow
              key={i}
              precondition={p}
              index={i}
              isNext={i === firstUnsatisfiedIdx}
            />
          ))}
        </div>
      )}

      {/* Outcome action — appears once all steps are satisfied, or immediately if there are no steps */}
      {member.nextAction && (allSatisfied || noSteps) && (
        <div className={`px-3 pb-2.5 pt-2 bg-primary/[0.06] ${hasVisibleRows ? 'border-t border-primary/10' : ''}`}>
          <WorkflowActionButton
            action={member.nextAction}
            disabled={false}
            testId="workflow-outcome-action-btn"
            compact
          />
        </div>
      )}
    </div>
  );
}

// ── Saga card ──────────────────────────────────────────────────────────────────

function SagaCard({ saga }: { saga: SagaWorkflowSummary }) {
  const { dismiss } = useWorkflowPanel();

  const completedCount = saga.members.filter((m) => m.status === 'completed').length;
  const totalCount = saga.members.length;
  const allComplete = completedCount === totalCount;
  const fallbackLabel = SAGA_FALLBACK_LABELS[saga.type];
  const unitLabel = realUnitNumber(saga.unitLabel) ?? fallbackLabel;

  return (
    <div
      data-testid={`workflow-saga-row-${saga.id}`}
      className="rounded-lg border border-border bg-background p-3"
      role="listitem"
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">{unitLabel}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {allComplete
              ? `${fallbackLabel} complete`
              : `${fallbackLabel} · ${completedCount} of ${totalCount} done`}
          </p>
        </div>
        <DismissWorkflowButton
          workflowId={saga.id}
          accessibleLabel={`Dismiss ${unitLabel} workflow`}
          testId={`workflow-dismiss-btn-${saga.id}`}
          onDismiss={dismiss}
          className="-mt-2 -mr-2"
        />
      </div>

      {/* Vertical member stage rows */}
      <div className="flex flex-col gap-1.5">
        {saga.members.map((member, i) => {
          const prevLabel =
            i > 0
              ? (MEMBER_TYPE_LABELS[saga.members[i - 1]!.type] ?? saga.members[i - 1]!.type)
              : undefined;

          if (member.status === 'completed') {
            return <CompletedStageRow key={member.id ?? `member-${i}`} member={member} />;
          }
          if (member.status === 'active') {
            return <ActiveStageRow key={member.id ?? `member-${i}`} member={member} />;
          }
          return (
            <UpcomingStageRow
              key={member.id ?? `member-${i}`}
              member={member}
              prevLabel={prevLabel}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Atomic card ────────────────────────────────────────────────────────────────

function AtomicCard({ workflow }: { workflow: AtomicWorkflowSummary }) {
  const { dismiss } = useWorkflowPanel();

  const label = ATOMIC_WORKFLOW_LABELS[workflow.type];
  const member = workflow.members[0];

  return (
    <div
      data-testid={`workflow-atomic-row-${workflow.id}`}
      className="rounded-lg border border-border bg-background p-3"
      role="listitem"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">{label}</p>
          {realUnitNumber(workflow.subjectLabel) && (
            <p className="text-xs text-muted-foreground mt-0.5">{realUnitNumber(workflow.subjectLabel)}</p>
          )}
        </div>
        <DismissWorkflowButton
          workflowId={workflow.id}
          accessibleLabel={`Dismiss ${label} workflow`}
          testId={`workflow-dismiss-btn-${workflow.id}`}
          onDismiss={dismiss}
          className="-mt-2 -mr-2"
        />
      </div>

      {member &&
        (member.status === 'completed' ? (
          <CompletedStageRow member={member} />
        ) : (
          <ActiveStageRow member={member} />
        ))}
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export function WorkflowRailContent() {
  const { workflows, isLoading, isError, isFetching, refetch } = useWorkflowPanel();

  const activeWorkflows = workflows.filter((w) => w.status !== 'dismissed');
  const showError = isError && activeWorkflows.length === 0;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-36 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );
  }

  if (showError) {
    return (
      <div
        data-testid="workflow-list-error"
        role="alert"
        className="flex flex-col items-center justify-center py-8 px-4 text-center"
      >
        <div
          className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3"
          aria-hidden="true"
        >
          <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">Couldn't load workflows</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">Something went wrong fetching your workflows.</p>
        <button
          type="button"
          data-testid="workflow-list-retry"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-busy={isFetching}
          className="px-4 min-h-[44px] text-sm font-medium text-primary border border-primary/40 rounded-md hover:bg-primary/5 disabled:opacity-60 disabled:cursor-default"
        >
          {isFetching ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    );
  }

  if (activeWorkflows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3" aria-hidden="true">
          <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">All caught up</p>
        <p className="text-xs text-muted-foreground mt-0.5">No active workflows</p>
      </div>
    );
  }

  return (
    <div
      data-testid="workflow-list"
      className="flex flex-col gap-3 p-4"
      role="list"
      aria-label="Active workflows"
    >
      {activeWorkflows.map((workflow) =>
        workflow.kind === 'saga' ? (
          <SagaCard key={workflow.id} saga={workflow} />
        ) : (
          <AtomicCard key={workflow.id} workflow={workflow} />
        ),
      )}
    </div>
  );
}
